import React, { useCallback, useMemo } from 'react';

import { LocalizableText } from '~/@components/LocalizableText';
import { ActionVariant, ITableCellAction, TableCellActions } from '~/@components/Table/TableCellActions';
import { deleteDeliveryLineFromProject } from '~/@user-store/deliveryLines/deliveryLine.requests';
import { ProjectWeightReceiptInfo } from '~/@user-store/weightReceipts/projectWeightReceipts.store';
import { useUserAbilities } from '~/contexts';
import { showConfirmDialog } from '~/services/dialog';
import { canDeleteDeliveryLine, canUpdateDeliveryLineBySender } from '~/utils/auth';

import { IProject } from '../../ProjectMassesDetails/types';

type Props = {
    line: ProjectWeightReceiptInfo;
    project: IProject;
    onEdit: () => void;
};

export const DeliveryOrderNumberCell = ({ line, onEdit, project }: Props) => {
    const ability = useUserAbilities();

    const canEdit = useMemo(
        () => canUpdateDeliveryLineBySender(ability, line, line.dumpLoad, project),
        [ability, line, project]
    );

    const canDelete = useMemo(
        () => canDeleteDeliveryLine(ability, line, line.dumpLoad, project),
        [ability, line, project]
    );

    const onDelete = useCallback(async () => {
        const confirmed = await showConfirmDialog({
            title: <LocalizableText code={'DeliveryLinesTable.deleteInfo'} />,
            message: <LocalizableText code={'DeliveryLinesTable.deleteConfirmation'} />,
        });

        if (!confirmed) return;

        await deleteDeliveryLineFromProject(project.id, line.id);
    }, [project, line]);

    const actions: Array<ITableCellAction> = useMemo(
        () => [
            {
                variant: ActionVariant.EDIT,
                onClick: onEdit,
                disabled: !canEdit,
            },
            {
                variant: ActionVariant.DELETE,
                onClick: onDelete,
                disabled: !canDelete || !canEdit,
            },
        ],
        [onEdit, canEdit, canDelete, onDelete]
    );

    return <TableCellActions actions={actions}>{line.serialNumber}</TableCellActions>;
};
